import { goldCoinTier } from "../shared/bounty.ts";
import { claimCardWords } from "../shared/claims.ts";

/**
 * Accessible names for the controls a screen reader would otherwise hear as a list of identical
 * buttons or as a card's photo placeholder: built from what the card or row already shows, in the
 * app's own words. Nothing here is visible; the visible text stays as it is.
 */

/** The coin tiers in words (src/components/Gold.tsx Coins draws them): one, two or three coins. */
export const COIN_WORDS = { 1: "a little gold", 2: "some gold", 3: "a lot of gold" } as const;

export interface TaskCardFacts {
  title: string;
  /** The NEW pill: new to this device. */
  isNew?: boolean;
  /** Posted by a schedule (the repeat icon). */
  repeats?: boolean;
  urgent?: boolean;
  luxury?: boolean;
  /** The gold the coins stand for; 0 or less when the card shows no coins. */
  gold?: number;
  /** A done card: who finished it and when, as the card prints it ("Today 3:42 PM", "Sep 22"). */
  done?: { by: string | null; at: string };
  /** "Me do it" (claimUI): who holds a live claim, said as "Hue is on it". */
  claimedBy?: string | null;
  /** The roulette stamp's words, "Assigned to Hue by roulette" (shared/peon-stamp.ts rouletteStampLabel). */
  roulette?: string | null;
}

/**
 * A board card's name: "Fix bins, repeats, urgent, a lot of gold"; a done card "Water the plants,
 * done by Tester, Sep 22". The states are the ones the card shows by colour or icon alone.
 */
export function taskCardLabel(f: TaskCardFacts): string {
  if (f.done) return [f.title, f.done.by ? `done by ${f.done.by}` : "done", f.done.at].filter(Boolean).join(", ");
  const parts = [f.title];
  if (f.isNew) parts.push("new");
  if (f.repeats) parts.push("repeats");
  if (f.urgent) parts.push("urgent");
  if (f.luxury) parts.push("luxury");
  if (f.claimedBy) parts.push(claimCardWords(f.claimedBy));
  if (f.roulette) parts.push(f.roulette);
  if (f.gold && f.gold > 0) parts.push(COIN_WORDS[goldCoinTier(f.gold)]);
  return parts.join(", ");
}

/** A schedule card's name: its title, its cadence and its next line ("Bins, Weekly on Monday at 9:00 AM, Next: ..."). */
export function scheduleCardLabel(title: string, lines: readonly (string | null | false | undefined)[]): string {
  return [title, ...lines].filter((s): s is string => typeof s === "string" && s.trim() !== "").join(", ");
}

/** An upgrade card's name: its title and its progress line ("Hot tub, 1,250 of 5,000 wood"). */
export function upgradeCardLabel(title: string, progress: string): string {
  return progress ? `${title}, ${progress}` : title;
}

/** Which device a House screen action is about: "Web", "Web, this device". */
function deviceWords(platform: string, thisDevice: boolean): string {
  return [platform, thisDevice ? "this device" : ""].filter(Boolean).join(", ");
}

/**
 * A device's actions in the House screen: "Make Boss5000 a peon, Web", "Sign out Boss5000, Web".
 * `who` is the name the row sits under; `platform` the device line's platform word.
 */
export function memberActionLabel(action: "peon" | "steward" | "sign-out", who: string, platform: string, thisDevice = false): string {
  const device = deviceWords(platform, thisDevice);
  const head = action === "sign-out" ? `Sign out ${who}` : `Make ${who} a ${action}`;
  return device ? `${head}, ${device}` : head;
}

/** An invite's actions: "Share invite ANEEYTAP", "Revoke invite ANEEYTAP", "Show QR for invite ANEEYTAP". */
export function inviteActionLabel(action: "Share" | "Revoke" | "Show QR" | "Hide QR", code: string): string {
  return action.endsWith("QR") ? `${action} for invite ${code}` : `${action} invite ${code}`;
}

/** A person's action in the House screen's People list: "Remove password for Omie". */
export function personActionLabel(action: string, name: string): string {
  return `${action} for ${name}`;
}

/** A reward card's action, naming the reward: "Got it, Pizza night". */
export function rewardActionLabel(action: string, title: string): string {
  return `${action}, ${title}`;
}
